"use client";

import {useState} from "react";
import {useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import {Loader} from "lucide-react";
import {env} from "next-runtime-env";
import {Button} from "@/components/ui/button";
import {Form, FormControl, FormField, FormItem, FormLabel, FormMessage} from "@/components/ui/form";
import {Input} from "@/components/ui/input";
import {Textarea} from "@/components/ui/textarea";
import {messageForFailedResponse, NETWORK_ERROR_MESSAGE} from "@/lib/form-errors";
import {z} from "@/lib/zod";

const schema = z.object({
    first_name: z.string().trim().min(1, "*Required").max(50),
    last_name: z.string().trim().min(1, "*Required").max(50),
    email: z.string().trim().email("This is not a valid email").max(254),
    phone: z.string().trim().max(25).regex(/^[0-9 +\-().]*$/, "Use digits, spaces and + - ( ) only").optional(),
    message: z.string().trim().min(1, "*Required").max(2000, "Please keep it under 2,000 characters"),
    // Honeypot: hidden from people, so only bots fill it in.
    website: z.string().optional(),
});
type Values = z.infer<typeof schema>;

/** "Ask about this listing": posts to the inquiry endpoint, which stores a lead and emails Leanne. */
export default function InquiryForm({listingKey, address, mlsNumber}: {
    listingKey: string;
    address: string | null;
    mlsNumber: string | null;
}) {
    const apiUri = env("NEXT_PUBLIC_API_URI");
    const [sent, setSent] = useState(false);
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const about = address ? `${address}${mlsNumber ? ` (MLS® ${mlsNumber})` : ""}` : "this listing";
    const form = useForm<Values>({
        resolver: zodResolver(schema),
        defaultValues: {
            first_name: "", last_name: "", email: "", phone: "", website: "",
            message: `I'd like to know more about ${about}.`,
        },
        mode: "onBlur",
    });

    async function onSubmit(values: Values) {
        setBusy(true);
        setError("");
        try {
            const res = await fetch(`${apiUri}/api/v1/listings/${encodeURIComponent(listingKey)}/inquiry`, {
                method: "POST",
                headers: {"Content-Type": "application/json"},
                body: JSON.stringify({...values, phone: values.phone || null}),
            });
            if (!res.ok) {
                setError(await messageForFailedResponse(res));
                return;
            }
            setSent(true);
            form.reset();
        } catch {
            setError(NETWORK_ERROR_MESSAGE);
        } finally {
            setBusy(false);
        }
    }

    if (sent) {
        return (
            <p role="status" className="rounded-md bg-green-50 p-4 text-sm text-green-800">
                Thank you. Your message has been sent and a team member from Leanne Robb PREC will get back to you soon.
            </p>
        );
    }

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} noValidate className="grid gap-4 sm:grid-cols-2"
                  aria-label="Ask about this listing">
                <FormField control={form.control} name="first_name" render={({field}) => (
                    <FormItem><FormLabel>First name</FormLabel>
                        <FormControl><Input autoComplete="given-name" {...field}/></FormControl><FormMessage/></FormItem>
                )}/>
                <FormField control={form.control} name="last_name" render={({field}) => (
                    <FormItem><FormLabel>Last name</FormLabel>
                        <FormControl><Input autoComplete="family-name" {...field}/></FormControl><FormMessage/></FormItem>
                )}/>
                <FormField control={form.control} name="email" render={({field}) => (
                    <FormItem><FormLabel>Email</FormLabel>
                        <FormControl><Input type="email" autoComplete="email" {...field}/></FormControl><FormMessage/></FormItem>
                )}/>
                <FormField control={form.control} name="phone" render={({field}) => (
                    <FormItem><FormLabel>Phone (optional)</FormLabel>
                        <FormControl><Input type="tel" autoComplete="tel" {...field}/></FormControl><FormMessage/></FormItem>
                )}/>
                <FormField control={form.control} name="message" render={({field}) => (
                    <FormItem className="sm:col-span-2"><FormLabel>Message</FormLabel>
                        <FormControl><Textarea rows={4} {...field}/></FormControl><FormMessage/></FormItem>
                )}/>
                {/* Honeypot: off-screen and inert, so people and screen readers never reach it. */}
                <div inert aria-hidden="true" className="absolute -left-[10000px] h-0 w-0 overflow-hidden">
                    <label htmlFor="inquiry-website">Leave this field empty</label>
                    <input id="inquiry-website" type="text" tabIndex={-1} autoComplete="off" {...form.register("website")}/>
                </div>
                <div className="sm:col-span-2 space-y-3">
                    <Button type="submit" variant="brand" disabled={busy}>
                        {busy ? <Loader className="animate-spin" aria-label="Sending"/> : "Send message"}
                    </Button>
                    {error ? <p role="alert" className="text-sm text-red-600">{error}</p> : null}
                </div>
            </form>
        </Form>
    );
}
